import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import { IconClose, IconUpload, IconDownload, IconCheck, IconShield } from "../icons";

/**
 * IMPORTAR COLABORADORES DA PLANILHA.
 *
 * Dois passos, de propósito: primeiro o sistema LÊ a planilha e mostra o que
 * vai acontecer (quem entra novo, quem é atualizado e o que muda em cada um,
 * o que será ignorado e por quê) — só depois o botão de confirmar grava.
 *
 * Importação que grava direto é como o RH perde dado sem perceber: aqui, se a
 * prévia mostrar algo estranho, é só fechar a janela.
 */

interface Previa {
  total: number;
  aba: string;
  abas: string[];
  novos: { linha: number; nome: string; cargo: string; status: string }[];
  atualizados: { linha: number; nome: string; por: string; mudancas: string[] }[];
  semMudanca: { linha: number; nome: string }[];
  ignoradas: { linha: number; motivo: string; conteudo: string }[];
  avisos: string[];
  colunasIgnoradas: string[];
  aplicado: boolean;
}

export default function ImportarColaboradoresModal({
  onClose,
  onImportado,
}: {
  onClose: () => void;
  onImportado: (mensagem: string) => void | Promise<void>;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [aba, setAba] = useState("");
  const [lendo, setLendo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function ler(file: File, abaEscolhida?: string) {
    setErro(null);
    setLendo(true);
    setArquivo(file);
    try {
      const r = (await api.planilha.importar(file, false, abaEscolhida)) as Previa;
      setPrevia(r);
      setAba(r.aba);
    } catch (e) {
      setPrevia(null);
      setErro(e instanceof ApiError ? e.message : "Não foi possível ler a planilha.");
    } finally {
      setLendo(false);
    }
  }

  async function confirmar() {
    if (!arquivo || !previa) return;
    setGravando(true);
    setErro(null);
    try {
      const r = (await api.planilha.importar(arquivo, true, aba)) as Previa;
      await onImportado(
        `Planilha importada: ${r.novos.length} colaborador(es) novo(s) e ${r.atualizados.length} atualizado(s).`,
      );
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Erro ao importar.");
      setGravando(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card import-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Importar colaboradores da planilha</h3>
            <p className="modal-sub">
              O sistema cruza por <strong>CPF</strong>, depois <strong>e-mail</strong> e depois{" "}
              <strong>nome</strong> — quem já existe é atualizado, não duplicado.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div
            className={`import-solta ${arrastando ? "ativa" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastando(true);
            }}
            onDragLeave={() => setArrastando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastando(false);
              const f = e.dataTransfer.files?.[0];
              if (f) ler(f);
            }}
          >
            <IconUpload size={22} />
            <div>
              <strong>{arquivo ? arquivo.name : "Arraste a planilha aqui"}</strong>
              <small className="muted">
                Arquivo .xlsx (Excel), até 12 MB. A planilha pode ter várias abas — o sistema
                acha a do cadastro.
              </small>
            </div>
            <button className="btn-secondary btn-sm" onClick={() => inputRef.current?.click()}>
              Escolher arquivo
            </button>
            <button
              className="btn-secondary btn-sm"
              onClick={() => api.planilha.baixar("modelo").catch((e) => setErro(e.message))}
              title="Baixar o modelo em branco"
            >
              <IconDownload size={15} /> Modelo
            </button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) ler(f);
                e.target.value = "";
              }}
            />
          </div>

          {erro && <div className="form-error">{erro}</div>}
          {lendo && <div className="empty">Lendo a planilha…</div>}

          {previa && !lendo && (
            <>
              {previa.abas.length > 1 && (
                <label className="field import-aba">
                  <span>Aba da planilha</span>
                  <select
                    value={aba}
                    onChange={(e) => {
                      setAba(e.target.value);
                      if (arquivo) ler(arquivo, e.target.value);
                    }}
                  >
                    {previa.abas.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <div className="import-resumo">
                <Cartao valor={previa.novos.length} rotulo="entram como novos" destaque />
                <Cartao valor={previa.atualizados.length} rotulo="serão atualizados" />
                <Cartao valor={previa.semMudanca.length} rotulo="já estão iguais" />
                <Cartao valor={previa.ignoradas.length} rotulo="linhas ignoradas" alerta={previa.ignoradas.length > 0} />
              </div>

              {previa.avisos.length > 0 && (
                <div className="import-avisos">
                  {previa.avisos.map((a) => (
                    <p key={a}>
                      <IconShield size={14} /> {a}
                    </p>
                  ))}
                </div>
              )}

              {previa.colunasIgnoradas.length > 0 && (
                <p className="muted import-nota">
                  Colunas da planilha que o cadastro não usa (ficam de fora):{" "}
                  {previa.colunasIgnoradas.join(", ")}.
                </p>
              )}

              {previa.novos.length > 0 && (
                <Secao titulo={`Novos (${previa.novos.length})`}>
                  <ul className="import-lista">
                    {previa.novos.map((n) => (
                      <li key={`n${n.linha}`}>
                        <span className="import-linha">linha {n.linha}</span>
                        <strong>{n.nome}</strong>
                        <small className="muted">
                          {n.cargo || "sem função"} · {n.status}
                        </small>
                      </li>
                    ))}
                  </ul>
                </Secao>
              )}

              {previa.atualizados.length > 0 && (
                <Secao titulo={`Atualizados (${previa.atualizados.length})`}>
                  <ul className="import-lista">
                    {previa.atualizados.map((a) => (
                      <li key={`a${a.linha}`}>
                        <span className="import-linha">linha {a.linha}</span>
                        <strong>{a.nome}</strong>
                        <small className="muted">casou por {a.por}</small>
                        <ul className="import-mudancas">
                          {a.mudancas.map((m) => (
                            <li key={m}>{m}</li>
                          ))}
                        </ul>
                      </li>
                    ))}
                  </ul>
                </Secao>
              )}

              {previa.ignoradas.length > 0 && (
                <Secao titulo={`Ignoradas (${previa.ignoradas.length})`}>
                  <ul className="import-lista">
                    {previa.ignoradas.map((i) => (
                      <li key={`i${i.linha}-${i.motivo}`}>
                        <span className="import-linha">linha {i.linha}</span>
                        <strong>{i.motivo}</strong>
                        <small className="muted">{i.conteudo}</small>
                      </li>
                    ))}
                  </ul>
                </Secao>
              )}
            </>
          )}
        </div>

        <div className="modal-foot">
          <span className="muted import-rodape">
            {previa
              ? `${previa.total} linha(s) de colaborador na aba "${previa.aba}"`
              : "Nada é gravado antes de você confirmar."}
          </span>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!previa || gravando || previa.novos.length + previa.atualizados.length === 0}
            onClick={confirmar}
          >
            <IconCheck size={16} />{" "}
            {gravando
              ? "Importando…"
              : previa
                ? `Confirmar (${previa.novos.length + previa.atualizados.length})`
                : "Confirmar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Cartao({
  valor,
  rotulo,
  destaque,
  alerta,
}: {
  valor: number;
  rotulo: string;
  destaque?: boolean;
  alerta?: boolean;
}) {
  return (
    <div className={`import-cartao ${destaque ? "destaque" : ""} ${alerta ? "alerta" : ""}`}>
      <strong>{valor}</strong>
      <span>{rotulo}</span>
    </div>
  );
}

function Secao({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <details className="import-secao" open>
      <summary>{titulo}</summary>
      {children}
    </details>
  );
}
